import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseAutoScrollOptions {
  /** Distance from the bottom (px) within which the user is considered "at bottom". Default 150. */
  threshold?: number
}

export interface UseAutoScrollReturn {
  scrollRef: React.RefObject<HTMLDivElement | null>
  /** Whether the scroll container is currently near the bottom. */
  isAtBottom: boolean
  /** Scroll to the bottom and re-enable auto-follow (smooth by default). */
  scrollToBottom: (options?: { behavior?: ScrollBehavior }) => void
}

/**
 * Auto-scroll a container to the bottom as content grows (via MutationObserver).
 *
 * While the user is "at bottom" (within `threshold` px), new content triggers
 * an instant scroll-down. When the user scrolls away, auto-follow pauses so
 * they can read earlier content. Calling `scrollToBottom()` smooth-scrolls
 * back and re-enables following.
 */
export function useAutoScroll(
  options: UseAutoScrollOptions = {},
): UseAutoScrollReturn {
  const { threshold = 150 } = options
  const scrollRef = useRef<HTMLDivElement | null>(null)

  // Authoritative proximity flag — updated on every rAF-throttled scroll tick.
  // Stored as a ref so the MutationObserver callback always sees the latest
  // value without triggering re-renders on every scroll event.
  const atBottomRef = useRef(true)

  // State mirror — only updated on transitions so React re-renders for the
  // floating button visibility toggle.
  const [isAtBottom, setIsAtBottom] = useState(true)

  // Guard flag for programmatic smooth scroll to bottom so intermediate
  // scroll events during the animation do not prematurely set isAtBottom to false.
  const isProgrammaticScrollRef = useRef(false)
  const programmaticTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  // rAF guard for scroll-event throttling.
  const rafId = useRef(0)

  const computeAtBottom = useCallback(
    (el: HTMLElement): boolean =>
      el.scrollHeight - el.scrollTop - el.clientHeight <= threshold,
    [threshold],
  )

  const scrollToBottom = useCallback(
    (options?: { behavior?: ScrollBehavior }) => {
      const el = scrollRef.current
      if (!el) return
      const behavior = options?.behavior ?? 'smooth'
      atBottomRef.current = true
      setIsAtBottom(true)

      if (behavior === 'smooth') {
        isProgrammaticScrollRef.current = true
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })

        if (programmaticTimeoutId.current) {
          clearTimeout(programmaticTimeoutId.current)
        }
        programmaticTimeoutId.current = setTimeout(() => {
          isProgrammaticScrollRef.current = false
          programmaticTimeoutId.current = null
        }, 1000)
      } else {
        isProgrammaticScrollRef.current = false
        if (programmaticTimeoutId.current) {
          clearTimeout(programmaticTimeoutId.current)
          programmaticTimeoutId.current = null
        }
        el.scrollTop = el.scrollHeight
      }
    },
    [],
  )

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    function clearProgrammaticScroll() {
      isProgrammaticScrollRef.current = false
      if (programmaticTimeoutId.current) {
        clearTimeout(programmaticTimeoutId.current)
        programmaticTimeoutId.current = null
      }
    }

    // ── Initial scroll to bottom on mount / attach ────────────────────────
    if (atBottomRef.current) {
      el.scrollTop = el.scrollHeight
    }
    const initialAtBottom = computeAtBottom(el)
    if (initialAtBottom !== atBottomRef.current) {
      atBottomRef.current = initialAtBottom
      setIsAtBottom(initialAtBottom)
    }

    // ── Scroll listener (rAF-throttled) ──────────────────────────────────
    function onScroll() {
      if (rafId.current) return
      rafId.current = requestAnimationFrame(() => {
        rafId.current = 0
        if (!el) return
        const now = computeAtBottom(el)
        if (now) {
          clearProgrammaticScroll()
        }
        if (isProgrammaticScrollRef.current) {
          return
        }
        if (now !== atBottomRef.current) {
          atBottomRef.current = now
          setIsAtBottom(now)
        }
      })
    }

    function onScrollEnd() {
      clearProgrammaticScroll()
      if (!el) return
      const now = computeAtBottom(el)
      if (now !== atBottomRef.current) {
        atBottomRef.current = now
        setIsAtBottom(now)
      }
    }

    function onUserInterrupt() {
      clearProgrammaticScroll()
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    el.addEventListener('scrollend', onScrollEnd, { passive: true })
    el.addEventListener('wheel', onUserInterrupt, { passive: true })
    el.addEventListener('touchstart', onUserInterrupt, { passive: true })

    // ── MutationObserver for content growth ───────────────────────────────
    const observer = new MutationObserver(() => {
      if (atBottomRef.current && el) {
        el.scrollTop = el.scrollHeight
      }
    })
    observer.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    return () => {
      el.removeEventListener('scroll', onScroll)
      el.removeEventListener('scrollend', onScrollEnd)
      el.removeEventListener('wheel', onUserInterrupt)
      el.removeEventListener('touchstart', onUserInterrupt)
      if (rafId.current) cancelAnimationFrame(rafId.current)
      if (programmaticTimeoutId.current) {
        clearTimeout(programmaticTimeoutId.current)
        programmaticTimeoutId.current = null
      }
      observer.disconnect()
    }
  }, [computeAtBottom])

  return { scrollRef, isAtBottom, scrollToBottom }
}
